import axios from "axios";
import { parseApiError } from "./parseApiError";
import { isPasswordExpiredError, setPasswordExpired } from "./passwordExpiry";

const isDevelopment = process.env.NODE_ENV === "development";

// Configure your API URLs via .env (for local) and .env.production (for build)
const baseURL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL,
  timeout: 60000, // Increased to 60 seconds for second-wise data fetching
});

export const getApiBase = () => baseURL;

// Uploaded media (e.g. machine pictures) is served OUTSIDE the /api path, like
// "/machine_pictures/x.jpg". On localhost the CRA proxy only forwards /api, so a
// relative <img src> would hit the dev server (3000) and 404. Resolve such URLs to
// the absolute backend origin (derived from the API base). <img> loads cross-origin fine.
const MEDIA_BASE = (baseURL || "").replace(/\/api\/?$/i, "");
export const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url; // already absolute
  const base = MEDIA_BASE || window.location.origin;
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Dedicated client for the forecasting endpoints (/mape, /get_forecasted_data).
// During local verification these can be served by the standalone read-only
// forecast server (local_forecast_server.py) so the badge/chart show the
// corrected values without touching the shared backend or its data.
// Set REACT_APP_FORECAST_API_URL (e.g. http://187.77.136.60:8010/api) to enable;
// otherwise it transparently falls back to the normal API base.
const FORECAST_API_BASE =
  window.__ENVVIZ_FORECAST_API_BASE ||
  process.env.REACT_APP_FORECAST_API_URL ||
  baseURL;

export const forecastApi = axios.create({
  baseURL: FORECAST_API_BASE,
  timeout: 60000,
});

const attachParsedApiError = (error) => {
  error.parsedError = parseApiError(error);
  return Promise.reject(error);
};

// A password can lapse while the dashboard is open. The backend starts
// answering 403 PASSWORD_EXPIRED on every endpoint at that moment, so raise
// the flag here rather than leaving the user with panels that silently fail.
const flagExpiredPassword = (error) => {
  if (isPasswordExpiredError(error)) {
    setPasswordExpired(true, error?.response?.data?.password_expires_at || null);
  }
  return Promise.reject(error);
};

api.interceptors.response.use((res) => res, attachParsedApiError);
forecastApi.interceptors.response.use((res) => res, attachParsedApiError);
api.interceptors.response.use((res) => res, flagExpiredPassword);

// If a request through the local CRA proxy returns 404 or a network error,
// some dev setups have issues with proxying. In that case, retry the request
// directly against the production API base (PROD_API_BASE). We only do this
// for requests that were originally targeted at the relative '/api' base.
// In-browser fallback retries to the production API are disabled because
// direct cross-origin attempts will be blocked by CORS when the backend
// doesn't allow the browser origin. If the CRA dev proxy fails (404),
// it's almost always a local proxy/config issue and should be fixed by
// restarting the dev server or adjusting `src/setupProxy.js`.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    try {
      const config = error.config || {};
      const status = error?.response?.status;

      // Only treat as a potential proxy issue if there's no response (network error)
      // or a 404 from the dev proxy. Auth errors like 401 should be handled by the app.
      if (
        config &&
        config.baseURL === "/api" &&
        (!status || status === 404)
      ) {
        // Provide a friendly, actionable console hint for developers
        console.error(
          "Dev proxy error: request to '/api' failed.",
          "If you're running locally, restart the dev server and check src/setupProxy.js and terminal logs.",
          {
            status,
            url: config.url,
            message: error.message,
          }
        );
      }
    } catch (e) {
      // ignore
    }
    return Promise.reject(error);
  },
);

// Add a helper to debug what's happening in production
api.interceptors.request.use((config) => {
  if (!isDevelopment) {
    console.log(
      `[API Request] ${config.method.toUpperCase()} ${config.baseURL}${config.url
      }`
    );
  }
  return config;
});

export default api;
